'use client';

import React, { forwardRef } from 'react';
import {
    Autocomplete as AriaAutocomplete,
    ListBox as AriaListBox,
    ListBoxItem as AriaListBoxItem,
    ListBoxLoadMoreItem as AriaListBoxLoadMoreItem,
    Tab as AriaTab,
    TabList as AriaTabList,
    TabPanel as AriaTabPanel,
    Tabs as AriaTabs,
    useFilter,
    type AutocompleteProps as AriaAutocompleteProps,
    type ListBoxItemProps as AriaListBoxItemProps,
    type ListBoxLoadMoreItemProps as AriaListBoxLoadMoreItemProps,
    type ListBoxProps as AriaListBoxProps,
    type TabListProps as AriaTabListProps,
    type TabPanelProps as AriaTabPanelProps,
    type TabProps as AriaTabProps,
    type TabsProps as AriaTabsProps,
} from 'react-aria-components';
import { cx, sortCx } from '../../../utils/cx';
import { utilityButtonStyles } from '../../base/button';
import { LoadingIndicator } from '../../base/loadingIndicator';
import { getNavItemClassNames, NavItemContent } from '../../base/navList';
import { AssetPickerSection, AssetPickerVirtualGrid } from './AssetPickerVirtualGrid';

const SEMANTIC_NAME = 'RomaAssetPicker';
const SEMANTIC_NAME_SIDEBAR = 'RomaAssetPickerSidebar';
const SEMANTIC_NAME_SIDEBAR_FOOTER = 'RomaAssetPickerSidebarFooter';
const SEMANTIC_NAME_TABS = 'RomaAssetPickerTabs';
const SEMANTIC_NAME_TAB_LIST = 'RomaAssetPickerTabList';
const SEMANTIC_NAME_TAB = 'RomaAssetPickerTab';
const SEMANTIC_NAME_PANE = 'RomaAssetPickerPane';
const SEMANTIC_NAME_GRID = 'RomaAssetPickerGrid';
const SEMANTIC_NAME_TILE = 'RomaAssetPickerTile';
const SEMANTIC_NAME_TAB_PANEL = 'RomaAssetPickerTabPanel';
const SEMANTIC_NAME_FILTER = 'RomaAssetPickerFilter';
const SEMANTIC_NAME_LOAD_MORE = 'RomaAssetPickerLoadMore';

// Shared by the frame and the load-more spacer, which holds a pane's worth of space.
const PICKER_HEIGHT_CLASS_NAME = 'h-134.5';

const PANE_CLASS_NAME = 'h-full w-99 shrink-0 scroll-py-3xl overflow-y-auto p-3xl outline-hidden';

// Figma's `_Nav item base` Size=lg (40px) matches Roma nav item `md`.
const TAB_SIZE = 'md';

const TAB_ROOT_CLASS_NAME = getNavItemClassNames({ size: TAB_SIZE }).root;

const tileStyles = sortCx({
    sm: 'size-8',
    // `AssetPicker.Grid` columns match this width.
    xl: 'size-14 rounded-xl',
    full: 'w-full rounded-none',
});

type AssetPickerTileSize = keyof typeof tileStyles;

export type AssetPickerProps = React.HTMLAttributes<HTMLDivElement>;
export type AssetPickerSidebarProps = React.HTMLAttributes<HTMLDivElement>;
export type AssetPickerSidebarFooterProps = React.HTMLAttributes<HTMLDivElement>;
export type AssetPickerPaneProps = React.HTMLAttributes<HTMLDivElement>;

export type AssetPickerTabsProps = Pick<AriaTabsProps, 'selectedKey' | 'onSelectionChange'> & {
    /** The picker frame, holding the tab list and the tab panel. */
    children?: React.ReactNode;
};

export type AssetPickerTabListProps<T extends object> = Pick<
    AriaTabListProps<T>,
    'items' | 'children' | 'aria-label'
> & {
    /** Additional CSS classes for the list. */
    className?: string;
};

export type AssetPickerTabPanelProps = Pick<AriaTabPanelProps, 'id'> & {
    /** Content for the selected tab. */
    children?: React.ReactNode;
    /** Additional CSS classes for the panel. */
    className?: string;
};

export type AssetPickerFilterProps = Omit<AriaAutocompleteProps<object>, 'filter'> & {
    /** Matches an option's `textValue` against the search text. Defaults to a case-insensitive substring match. */
    filter?: AriaAutocompleteProps<object>['filter'];
};

// Disabled states have no styles yet, so the parts don't accept them.
export type AssetPickerTabProps = Omit<AriaTabProps, 'children' | 'className' | 'isDisabled'> & {
    /** Tab label. Truncates when it overflows the sidebar. */
    children?: React.ReactNode;
    /** Additional CSS classes for the tab row. */
    className?: string;
};

export type AssetPickerGridProps<T extends object> = Omit<
    AriaListBoxProps<T>,
    'layout' | 'className' | 'disabledKeys' | 'disabledBehavior'
> & {
    /** Additional CSS classes for the grid. */
    className?: string;
};

export type AssetPickerLoadMoreProps = Pick<AriaListBoxLoadMoreItemProps, 'isLoading' | 'onLoadMore'> & {
    /** Announced while loading, e.g. "Loading more images". The spinner itself is hidden from screen readers. */
    label: string;
};

export type AssetPickerTileProps = Omit<AriaListBoxItemProps, 'className' | 'isDisabled'> & {
    /** Tile size: `xl` (56px) for board icons, `sm` (32px) for emoji, `full` (column width) for images. */
    size: AssetPickerTileSize;
    /** Additional CSS classes for the tile. */
    className?: string;
};

type AssetPickerCompoundComponents = {
    Sidebar: typeof AssetPickerSidebar;
    SidebarFooter: typeof AssetPickerSidebarFooter;
    Tabs: typeof AssetPickerTabs;
    TabList: typeof AssetPickerTabList;
    Tab: typeof AssetPickerTab;
    TabPanel: typeof AssetPickerTabPanel;
    Pane: typeof AssetPickerPane;
    Filter: typeof AssetPickerFilter;
    Grid: typeof AssetPickerGrid;
    VirtualGrid: typeof AssetPickerVirtualGrid;
    Section: typeof AssetPickerSection;
    Tile: typeof AssetPickerTile;
    LoadMore: typeof AssetPickerLoadMore;
};

const AssetPickerRoot = forwardRef<HTMLDivElement, AssetPickerProps>(({ className, ...props }, ref) => (
    <div
        {...props}
        ref={ref}
        // `min-h-0` lets the picker shrink below its height when a popover caps it to the window
        className={cx(SEMANTIC_NAME, PICKER_HEIGHT_CLASS_NAME, 'flex min-h-0 w-160 overflow-hidden', className)}
    />
));

const AssetPickerSidebar = forwardRef<HTMLDivElement, AssetPickerSidebarProps>(({ className, ...props }, ref) => (
    <div
        {...props}
        ref={ref}
        className={cx(SEMANTIC_NAME_SIDEBAR, 'flex w-61 shrink-0 flex-col gap-xl p-2xl', className)}
    />
));

const AssetPickerSidebarFooter = forwardRef<HTMLDivElement, AssetPickerSidebarFooterProps>(
    ({ className, ...props }, ref) => (
        <div
            {...props}
            ref={ref}
            className={cx(SEMANTIC_NAME_SIDEBAR_FOOTER, 'flex shrink-0 flex-col gap-lg', className)}
        />
    ),
);

/**
 * Gives tab pickers (icons, images) React Aria tab semantics across both columns,
 * so the selected tab controls the `TabPanel` beside it.
 * Renders no box of its own: wrap it around `AssetPicker`.
 */
const AssetPickerTabs: React.FC<AssetPickerTabsProps> = (props) => (
    <AriaTabs {...props} orientation="vertical" className="contents" />
);

const AssetPickerTabList = <T extends object>({ className, ...props }: AssetPickerTabListProps<T>) => (
    <AriaTabList
        {...props}
        className={cx(SEMANTIC_NAME_TAB_LIST, 'flex min-h-0 flex-1 flex-col overflow-y-auto', className)}
    />
);

/**
 * Renders a React Aria tab as a Roma nav item, so the tab list keeps arrow-key movement and tab semantics.
 */
const AssetPickerTab: React.FC<AssetPickerTabProps> = (props) => {
    const { children, className, ...rest } = props;

    return (
        <AriaTab {...rest} className={cx(SEMANTIC_NAME_TAB, TAB_ROOT_CLASS_NAME, className)}>
            {({ isSelected }) => (
                <span className={getNavItemClassNames({ size: TAB_SIZE, current: isSelected }).content}>
                    <NavItemContent truncate>{children}</NavItemContent>
                </span>
            )}
        </AriaTab>
    );
};

/**
 * Right column for pickers without tabs, such as emoji.
 */
const AssetPickerPane = forwardRef<HTMLDivElement, AssetPickerPaneProps>(({ className, ...props }, ref) => (
    <div {...props} ref={ref} className={cx(SEMANTIC_NAME_PANE, PANE_CLASS_NAME, className)} />
));

/**
 * Right column for tab pickers. Pass the selected tab's key as `id`; it renders only while that tab is selected.
 * Keyed by `id`, so each tab opens scrolled to the top with fresh grid state.
 */
const AssetPickerTabPanel: React.FC<AssetPickerTabPanelProps> = ({ id, className, ...props }) => (
    <AriaTabPanel {...props} key={id} id={id} className={cx(SEMANTIC_NAME_TAB_PANEL, PANE_CLASS_NAME, className)} />
);

/**
 * Filters the grid as the user types in a Roma `Input` inside it, e.g. emoji search.
 * Arrow keys in the input move through the results. Wrap it around `AssetPicker`.
 */
const AssetPickerFilter: React.FC<AssetPickerFilterProps> = ({ filter, ...props }) => {
    const { contains } = useFilter({ sensitivity: 'base' });

    return <AriaAutocomplete {...props} filter={filter ?? contains} />;
};

/**
 * A listbox laid out as a grid: each tile is an option to pick, like the React Aria emoji picker example.
 * Tiles sit five to a row, edge to edge, matching the board icon grid in Figma.
 * Columns are the `xl` tile width, so a part-filled last row keeps the spacing of a full one.
 */
const AssetPickerGridRoot = <T extends object>(
    { className, ...props }: AssetPickerGridProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
) => (
    <AriaListBox
        {...props}
        ref={ref}
        layout="grid"
        className={cx(
            SEMANTIC_NAME_GRID,
            'grid grid-cols-[repeat(5,--spacing(14))] justify-between gap-y-xl',
            className,
        )}
    />
);

const AssetPickerGridWithRef = forwardRef(AssetPickerGridRoot);
AssetPickerGridWithRef.displayName = SEMANTIC_NAME_GRID;

const AssetPickerGrid = AssetPickerGridWithRef as <T extends object>(
    props: AssetPickerGridProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

/**
 * Loads the next page when it scrolls into view, e.g. image search results.
 * Place it after the tiles; it shows a spinner while loading.
 * Loading starts two pane heights early, and the spinner holds a pane of space,
 * so a fast scroll runs into the spinner rather than bouncing off the end of the list.
 */
const AssetPickerLoadMore: React.FC<AssetPickerLoadMoreProps> = ({ label, ...props }) => (
    <AriaListBoxLoadMoreItem
        {...props}
        scrollOffset={2}
        className={cx(
            SEMANTIC_NAME_LOAD_MORE,
            PICKER_HEIGHT_CLASS_NAME,
            'col-span-full flex items-start justify-center py-xl',
        )}
    >
        <LoadingIndicator.Spinner />
        <span className="sr-only">{label}</span>
    </AriaListBoxLoadMoreItem>
);

/**
 * Renders the option itself as the tile, so there is no nested button to focus.
 * Icon and image tiles have no text, so `textValue` also names the option for screen readers.
 */
const AssetPickerTile: React.FC<AssetPickerTileProps> = ({ size, className, 'aria-label': ariaLabel, ...props }) => (
    <AriaListBoxItem
        {...props}
        aria-label={ariaLabel ?? props.textValue}
        className={({ isSelected }) =>
            cx(
                SEMANTIC_NAME_TILE,
                utilityButtonStyles.common.root,
                // `data-focus-visible` rather than `:focus-visible`: search moves focus virtually, without real focus
                'text-fg-primary data-[focus-visible]:outline-2 data-[focus-visible]:-outline-offset-1',
                tileStyles[size],
                isSelected && utilityButtonStyles.common.selected.ring,
                className,
            )
        }
    />
);

AssetPickerRoot.displayName = SEMANTIC_NAME;
AssetPickerSidebar.displayName = SEMANTIC_NAME_SIDEBAR;
AssetPickerSidebarFooter.displayName = SEMANTIC_NAME_SIDEBAR_FOOTER;
AssetPickerTabs.displayName = SEMANTIC_NAME_TABS;
AssetPickerTabList.displayName = SEMANTIC_NAME_TAB_LIST;
AssetPickerTab.displayName = SEMANTIC_NAME_TAB;
AssetPickerPane.displayName = SEMANTIC_NAME_PANE;
AssetPickerTabPanel.displayName = SEMANTIC_NAME_TAB_PANEL;
AssetPickerFilter.displayName = SEMANTIC_NAME_FILTER;
AssetPickerTile.displayName = SEMANTIC_NAME_TILE;
AssetPickerLoadMore.displayName = SEMANTIC_NAME_LOAD_MORE;

export const AssetPicker = Object.assign(AssetPickerRoot, {
    Sidebar: AssetPickerSidebar,
    SidebarFooter: AssetPickerSidebarFooter,
    Tabs: AssetPickerTabs,
    TabList: AssetPickerTabList,
    Tab: AssetPickerTab,
    TabPanel: AssetPickerTabPanel,
    Pane: AssetPickerPane,
    Filter: AssetPickerFilter,
    Grid: AssetPickerGrid,
    VirtualGrid: AssetPickerVirtualGrid,
    Section: AssetPickerSection,
    Tile: AssetPickerTile,
    LoadMore: AssetPickerLoadMore,
}) as typeof AssetPickerRoot & AssetPickerCompoundComponents;
